import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { apiService } from '../../../../components/api/apiService';
import { getConnectorDisplayName } from '../../Connections/catalog';
import { listDataStores } from '../../Connections/dataStoreApi';
import { logDataModels } from '../dataModelsLogger';
import type { DataSource, DataSourceKind, SpaceIconKey } from '../dataModelsTypes';
import { DialogShell, SourceIconTile, fieldClass, ghostBtn, labelClass, primaryBtn, searchClass } from './dataModelsUi';

type AuthUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roleName?: string;
  isActive?: boolean;
};

export type CreateSpaceInput = {
  name: string;
  description: string;
  icon: SpaceIconKey;
  owner: string;
  tags: string[];
  sourceIds: string[];
  sources: DataSource[];
};

export default function CreateSpacePicker({
  isLight,
  onClose,
  onSubmit,
}: {
  isLight: boolean;
  onClose: () => void;
  onSubmit: (input: CreateSpaceInput) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState('');
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [sourceQuery, setSourceQuery] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [sources, setSources] = useState<DataSource[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [sourcesError, setSourcesError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSourcesLoading(true);
    setSourcesError('');
    listDataStores()
      .then((rows) => {
        if (cancelled) return;
        setSources(rows.map(toConnectionSource));
        logDataModels('app', 'space_sources_loaded', { count: rows.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load connections';
        setSources([]);
        setSourcesError(message);
        logDataModels('error', 'space_sources_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setSourcesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    setUsersError('');
    apiService
      .get('/api/auth/users', {}, 'backend')
      .then((response: unknown) => {
        if (cancelled) return;
        const rows = Array.isArray(response) ? (response as AuthUser[]) : [];
        const active = rows.filter((user) => user?.id && user.isActive !== false);
        setUsers(active);
        logDataModels('app', 'space_owners_loaded', { count: active.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load users';
        setUsers([]);
        setUsersError(message);
        logDataModels('error', 'space_owners_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOwner = users.find((user) => user.id === owner);

  const visibleSources = useMemo(() => {
    const query = sourceQuery.trim().toLowerCase();
    if (!query) return sources;
    return sources.filter((source) =>
      `${source.name} ${source.connectionType || ''} ${source.kind} ${source.environment}`.toLowerCase().includes(query)
    );
  }, [sourceQuery, sources]);

  const collectedTags = (raw: string, current: string[]) => {
    const next = raw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const seen = new Set(current.map((tag) => tag.toLowerCase()));
    const merged = [...current];
    next.forEach((tag) => {
      if (seen.has(tag.toLowerCase()) || merged.length >= 12) return;
      seen.add(tag.toLowerCase());
      merged.push(tag);
    });
    return merged;
  };

  const resolvedTags = collectedTags(tagDraft, tags);
  const errors = {
    name: !name.trim(),
    description: !description.trim(),
    owner: !owner,
    sources: sourceIds.length === 0,
    tags: resolvedTags.length === 0,
  };

  const addTags = (raw: string) => {
    if (!raw.trim()) return;
    setTags((prev) => collectedTags(raw, prev));
    setTagDraft('');
  };

  const toggleSource = (id: string) => {
    setSourceIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const submit = () => {
    setAttempted(true);
    const finalTags = collectedTags(tagDraft, tags);
    if (tagDraft.trim()) setTags(finalTags);
    setTagDraft('');
    if (!name.trim() || !description.trim() || !owner || sourceIds.length === 0 || finalTags.length === 0) {
      logDataModels('warning', 'space_create_blocked', {
        reason: 'missing_required',
        name: Boolean(name.trim()),
        description: Boolean(description.trim()),
        owner: Boolean(owner),
        sources: sourceIds.length,
        tags: finalTags.length,
      });
      return;
    }
    const ownerName = selectedOwner ? userLabel(selectedOwner) : '';
    const input: CreateSpaceInput = {
      name: name.trim(),
      description: description.trim(),
      icon: iconFor(name.trim()),
      owner: ownerName,
      tags: finalTags,
      sourceIds,
      sources: sources.filter((source) => sourceIds.includes(source.id)),
    };
    logDataModels('app', 'space_create_submitted', {
      name: input.name,
      ownerId: owner,
      owner: ownerName,
      tags: input.tags,
      sourceIds,
    });
    onSubmit(input);
  };

  return (
    <DialogShell
      title="Create WorkSpace"
      subtitle="Name, description, owner, data sources, and tags are required."
      isLight={isLight}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            Create WorkSpace
          </button>
        </>
      }
    >
      <Field label="Name" isLight={isLight} error={attempted && errors.name ? 'Name is required.' : ''}>
        <input
          className={fieldClass(isLight)}
          value={name}
          autoFocus
          placeholder="Customer"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Description" isLight={isLight} error={attempted && errors.description ? 'Description is required.' : ''}>
        <textarea
          className={`${fieldClass(isLight)} min-h-[72px] resize-none`}
          value={description}
          placeholder="What this WorkSpace is for"
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field label="Owner" isLight={isLight} error={attempted && errors.owner ? 'Owner is required.' : ''}>
        <OwnerDropdown
          isLight={isLight}
          users={users}
          loading={usersLoading}
          error={usersError}
          value={owner}
          onChange={setOwner}
        />
      </Field>
      <Field
        label="Data sources"
        hint={sourceIds.length ? `${sourceIds.length} selected` : 'Select one or more'}
        isLight={isLight}
        error={attempted && errors.sources ? 'Select at least one data source.' : ''}
      >
        <div className="relative">
          <Search size={14} className={`pointer-events-none absolute left-2.5 top-2 ${isLight ? 'text-[#9AA1AB]' : 'text-slate-500'}`} />
          <input
            className={searchClass(isLight)}
            value={sourceQuery}
            placeholder="Search sources"
            onChange={(event) => setSourceQuery(event.target.value)}
          />
        </div>
        <div className={`mt-2 max-h-44 overflow-y-auto rounded-md border ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
          {sourcesLoading ? (
            <p className={`px-3 py-4 text-sm ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>Loading connections…</p>
          ) : sourcesError ? (
            <p className="px-3 py-4 text-sm text-rose-600">{sourcesError}</p>
          ) : visibleSources.length ? (
            visibleSources.map((source) => {
              const checked = sourceIds.includes(source.id);
              return (
                <label
                  key={source.id}
                  className={`flex cursor-pointer items-center gap-2.5 border-b px-2.5 py-2 last:border-b-0 ${
                    isLight ? 'border-[#F0F2F4] hover:bg-[#F7F8FA]' : 'border-white/10 hover:bg-white/5'
                  } ${checked ? (isLight ? 'bg-[#F7F8FA]' : 'bg-white/5') : ''}`}
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 shrink-0 accent-[#4B5563]"
                    checked={checked}
                    onChange={() => toggleSource(source.id)}
                  />
                  <SourceIconTile kind={source.kind} isLight={isLight} box="h-7 w-7" />
                  <span className="min-w-0">
                    <span className={`block truncate text-sm ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{source.name}</span>
                    <span className={`block truncate text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
                      {connectionLabel(source)}
                    </span>
                  </span>
                </label>
              );
            })
          ) : (
            <p className={`px-3 py-4 text-sm ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
              {sources.length ? 'No connections match.' : 'No connections yet. Create one from Connections.'}
            </p>
          )}
        </div>
      </Field>
      <Field label="Tags" hint={tags.length ? `${tags.length} added` : 'At least one'} isLight={isLight} error={attempted && errors.tags ? 'Add at least one tag.' : ''}>
        <TagEditor isLight={isLight} tags={tags} draft={tagDraft} onDraft={setTagDraft} onAdd={addTags} onRemove={(tag) => setTags((prev) => prev.filter((item) => item !== tag))} />
      </Field>
    </DialogShell>
  );
}

function Field({
  label,
  hint,
  error,
  isLight,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  isLight: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label className={`${labelClass(isLight)} mb-0`}>
          {label}
          <span className={isLight ? 'text-[#6B7280]' : 'text-slate-500'}> *</span>
        </label>
        {hint ? <span className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-500'}`}>{hint}</span> : null}
      </div>
      {children}
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

const SUGGESTED_TAGS = ['Analytics', 'PII', 'Customer', 'Revenue', 'Finance', 'Operations', 'Compliance', 'Restricted'];

export function TagEditor({
  isLight,
  tags,
  draft,
  onDraft,
  onAdd,
  onRemove,
}: {
  isLight: boolean;
  tags: string[];
  draft: string;
  onDraft: (value: string) => void;
  onAdd: (raw: string) => void;
  onRemove: (tag: string) => void;
}) {
  const taken = new Set(tags.map((tag) => tag.toLowerCase()));
  const query = draft.trim().toLowerCase();
  const suggestions = SUGGESTED_TAGS.filter((tag) => !taken.has(tag.toLowerCase()) && (!query || tag.toLowerCase().includes(query)));
  const canAdd = Boolean(draft.trim()) && !taken.has(draft.trim().toLowerCase());

  return (
    <div>
      <div className="flex gap-2">
        <input
          className={fieldClass(isLight)}
          value={draft}
          placeholder="Type a tag"
          onChange={(event) => onDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              onAdd(draft);
            }
          }}
        />
        <button type="button" className={`${ghostBtn(isLight)} shrink-0`} disabled={!canAdd} onClick={() => onAdd(draft)}>
          Add
        </button>
      </div>
      {tags.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className={`inline-flex items-center gap-1 rounded-full py-1 pl-2.5 pr-1 text-xs ${isLight ? 'bg-[#F2F4F7] text-[#1C1C1C]' : 'bg-white/10 text-white'}`}
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${isLight ? 'text-[#6B7280] hover:bg-[#E6E8EB]' : 'text-slate-300 hover:bg-white/10'}`}
                onClick={() => onRemove(tag)}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {suggestions.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`rounded-full border px-2.5 py-1 text-xs ${isLight ? 'border-[#E6E8EB] text-[#3C4048] hover:bg-[#F7F8FA]' : 'border-white/15 text-slate-300 hover:bg-white/5'}`}
              onClick={() => onAdd(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function OwnerDropdown({
  isLight,
  users,
  loading,
  error,
  value,
  onChange,
}: {
  isLight: boolean;
  users: AuthUser[];
  loading: boolean;
  error: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);
  const selected = users.find((user) => user.id === value);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) => userSearchText(user).includes(needle));
  }, [query, users]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const closedLabel = loading ? 'Loading users…' : error ? 'Unable to load users' : selected ? userLabel(selected) : 'Select an owner';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={`${fieldClass(isLight)} flex items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((next) => !next)}
      >
        <span className="min-w-0 flex-1">
          <span className={`block truncate ${selected ? '' : isLight ? 'text-[#9AA1AB]' : 'text-slate-500'}`}>{closedLabel}</span>
          {selected ? <span className={`block truncate text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{userDetail(selected)}</span> : null}
        </span>
        <ChevronDown size={14} className={`shrink-0 ${open ? 'rotate-180' : ''} ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`} />
      </button>
      {open ? (
        <div className={`absolute z-30 mt-1 w-full overflow-hidden rounded-md border shadow-lg ${isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-black'}`}>
          <div className={`flex items-center gap-2 border-b px-2.5 py-2 ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
            <Search size={14} className={isLight ? 'text-[#9AA1AB]' : 'text-slate-500'} />
            <input
              className={`w-full bg-transparent text-sm outline-none ${isLight ? 'text-[#1C1C1C] placeholder:text-[#9AA1AB]' : 'text-white placeholder:text-slate-500'}`}
              value={query}
              autoFocus
              placeholder="Filter users"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <ul className="max-h-44 overflow-y-auto py-1" role="listbox">
            {loading ? (
              <li className={`px-3 py-3 text-sm ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>Loading users…</li>
            ) : error ? (
              <li className="px-3 py-3 text-sm text-rose-600">{error}</li>
            ) : filtered.length ? (
              filtered.map((user) => {
                const checked = user.id === value;
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      className={`flex w-full flex-col px-3 py-2 text-left ${
                        checked
                          ? isLight
                            ? 'bg-[#F7F8FA]'
                            : 'bg-white/10'
                          : isLight
                            ? 'hover:bg-[#F7F8FA]'
                            : 'hover:bg-white/5'
                      }`}
                      onClick={() => {
                        onChange(user.id);
                        setOpen(false);
                      }}
                    >
                      <span className={`truncate text-sm ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{userLabel(user)}</span>
                      <span className={`truncate text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{userDetail(user)}</span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className={`px-3 py-3 text-sm ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
                {users.length ? 'No users match.' : 'No users found.'}
              </li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function userLabel(user: AuthUser): string {
  const name = [user.firstName, user.lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ');
  return name || user.email || 'User';
}

function userDetail(user: AuthUser): string {
  const role = (user.roleName || '').trim();
  if (user.email && role) return `${user.email} · ${role}`;
  return user.email || role;
}

function userSearchText(user: AuthUser): string {
  return `${userLabel(user)} ${user.email || ''} ${user.roleName || ''}`.toLowerCase();
}

function connectionLabel(source: DataSource): string {
  const type = source.connectionType || source.kind;
  return source.environment ? `${type} · ${source.environment}` : type;
}

function toConnectionSource(row: { id: string; name: string; connectorKey: string; environment?: string }): DataSource {
  return {
    id: row.id,
    name: row.name,
    kind: kindForConnector(row.connectorKey),
    environment: row.environment || '',
    connectionType: getConnectorDisplayName(row.connectorKey),
    connectorKey: row.connectorKey,
  };
}

function kindForConnector(connectorKey: string): DataSourceKind {
  const key = connectorKey.trim().toLowerCase();
  if (key === 'snowflake') return 'snowflake';
  if (key === 'mongodb' || key === 'firestore') return 'mongodb';
  if (key === 'bigquery') return 'bigquery';
  if (key === 'mssql' || key === 'sqlserver') return 'sqlserver';
  if (key === 'databricks') return 'databricks';
  return 'postgresql';
}

function iconFor(name: string): SpaceIconKey {
  const key = name.toLowerCase();
  if (/sale|revenue|market/.test(key)) return 'sales';
  if (/finan|bill|pay/.test(key)) return 'finance';
  if (/hr|people|talent/.test(key)) return 'hr';
  if (/risk|complian/.test(key)) return 'risk';
  if (/it|secur/.test(key)) return 'it';
  if (/product|sku/.test(key)) return 'product';
  if (/ops|operat/.test(key)) return 'operations';
  return 'customer';
}
